import React, { useCallback } from 'react';
import useStyles from './styles';
import { NextComponentType, NextPageContext } from 'next';
import Modal from '@mui/material/Modal';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { Subtract } from 'utility-types';

export interface WithProgressTypes {
  startProcessing: () => void;
  stopProcessing: () => void;
  setProcessingText: (value: string) => void;
  isProcessing: boolean;
}

/**
 * HOC that provides a blocking progress modal overlay for long-running async operations.
 * Prevents user interaction while processing and displays progress messages.
 *
 * @param Component - The component to wrap with progress functionality
 * @returns Wrapped component with progress control methods (startProcessing, stopProcessing, setProcessingText, isProcessing)
 *
 * More Info: https://www.pluralsight.com/guides/higher-order-composition-typescript-react
 */
const withProgress = <T extends WithProgressTypes>(
  Component: NextComponentType<NextPageContext, any, Subtract<T, WithProgressTypes>>,
) => {
  const WrappedComponent = (props: Subtract<T, WithProgressTypes>) => {
    const { classes } = useStyles();
    const [processing, setProcessing] = React.useState(false);
    const [displayText, setDisplayText] = React.useState<string>('');

    const startProcessing = useCallback(() => {
      setProcessing(true);
    }, []);

    const stopProcessing = useCallback(() => {
      setProcessing(false);
    }, []);

    const setProcessingText = useCallback((value: string) => {
      setDisplayText(value);
    }, []);

    // Prevent modal from closing via backdrop click or escape key
    const handleModalClose = useCallback(
      (_event: {}, reason: string) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          stopProcessing();
        }
      },
      [stopProcessing],
    );

    return (
      <>
        <Modal
          open={processing}
          onClose={handleModalClose}
          aria-labelledby="processing-modal-title"
          aria-describedby="processing-modal-description"
          disableEscapeKeyDown
          disableAutoFocus
        >
          <div className={classes.modalContentContainer}>
            <CircularProgress color="inherit" aria-label="Processing" />
            {displayText && (
              <Typography
                id="processing-modal-description"
                className={classes.displayText}
              >
                {displayText}
              </Typography>
            )}
          </div>
        </Modal>
        <Component
          {...props}
          startProcessing={startProcessing}
          stopProcessing={stopProcessing}
          setProcessingText={setProcessingText}
          isProcessing={processing}
        />
      </>
    );
  };

  const name = Component.displayName || Component.name || 'Component';
  WrappedComponent.displayName = `withProgress(${name})`;

  return WrappedComponent;
};

export default withProgress;
